/**
 * MenuBackdrop3D — the lazy-loaded, failure-guarded live 3D tavern-bar
 * backdrop for the main MENU screens (Home, Hall of Fame, and the other
 * interior menu stages). It is the sibling of {@link RoomBackdrop3D}: both
 * lazy-load the SAME {@link RoomBackdrop3DScene} chunk, so the heavy
 * `three` + `@tt/shell-3d` code is shared and only ever downloads once a
 * 3D surface actually mounts — it never weighs the gate / first-paint
 * bundle for users who bounce.
 *
 * The menu variant renders the scene with NO seated players: just the empty,
 * dim, warm premium tavern bar behind the readable 2D UI. It sits behind the
 * existing 2D {@link Background} SVG (mounted AFTER it in App.tsx, same
 * `z-index:-1` fixed layer, so it paints on top of the SVG only when WebGL
 * actually works) and is `pointer-events:none`, so every menu control stays
 * clickable on top.
 *
 * Guarding mirrors RoomBackdrop3D exactly:
 *  - {@link Lobby3DBoundary} catches any chunk-load / render throw and renders
 *    nothing, so the 2D SVG underneath shows through (never a black screen).
 *  - `<Suspense fallback={null}>` shows the 2D backdrop until the chunk lands.
 *  - The scene itself fully disposes the WebGL context + `forceContextLoss`
 *    on unmount, so navigating between menu screens never leaks contexts.
 *
 * Use THIS (or RoomBackdrop3D) from screens — never the scene module directly.
 */
import { Suspense, lazy } from 'react';
import type { CharacterIcon } from '@tt/types';
import { Lobby3DBoundary } from './Lobby3DBoundary';

// Same dynamic import specifier as RoomBackdrop3D → one shared Three chunk.
const RoomBackdrop3DScene = lazy(() => import('./RoomBackdrop3DScene'));

export interface MenuBackdrop3DProps {
  /** The active character's cosmetic icon — the strolling 3D hero. */
  heroIcon?: CharacterIcon;
  /**
   * How strongly to calm the hall behind the central content column (0..1).
   * Dense panels (Season + Achievements) pass a high value so every tile +
   * label stays legible over the scene.
   */
  contentDim?: number;
}

export function MenuBackdrop3D({ heroIcon, contentDim }: MenuBackdrop3DProps) {
  return (
    <Lobby3DBoundary>
      <Suspense fallback={null}>
        {/* No seated players — the premium tavern hall + navigation doorways. */}
        <RoomBackdrop3DScene
          variant="menu"
          {...(heroIcon ? { heroIcon } : {})}
          {...(contentDim !== undefined ? { contentDim } : {})}
        />
      </Suspense>
    </Lobby3DBoundary>
  );
}
